/**
 * Construct a new Vec3f object.<br><br>
 * 
 * #### new Vec3f(x, y, z)
 * #### new Vec3f([x, y, z])
 * 
 * where x, y, and z are the components of the vector.
 * 
 * @class The Vec3f object corresponds to a VRML SFVec3f field. Each component of the vector can be accessed using the x, y, and z properties.
 */
function Vec3f(/*x, y, z*/) {
    if (arguments.length == 3 && typeof arguments[0] == 'number') {
        /**
         * First value of the vector.
         * @type number
         * @default 0
         */
        this.x = arguments[0];
        /**
         * Second value of the vector.
         * @type number
         * @default 0
         */
        this.y = arguments[1];
        /**
         * Third value of the vector.
         * @type number
         * @default 0
         */
        this.z = arguments[2];
    } else if (arguments.length == 1 && Array.isArray(arguments[0]) && arguments[0].length === 3) {
        this.x = arguments[0][0];
        this.y = arguments[0][1];
        this.z = arguments[0][2];
    } else {
        this.x = 0;
        this.y = 0;
        this.z = 0;
    }
}

/**
 * Returns array represents vectors fields [x, y, z]
 * @returns {number[]} Array of three float
 */
Vec3f.prototype.toArray =
    function () {
        return [this.x, this.y, this.z];
    };

 /**
 * Returns a string containing the value of x, y, z separated by spaces.
 * @returns {string} 
 */
Vec3f.prototype.toString =
    function () {
        return this.toArray().join(' ');
    };

/**
 * Returns the value of the passed value added, component-wise, to the object.
 * @param {Vec3f} oVec3f The Vec3f object
 * @returns {Vec3f}
 */
Vec3f.prototype.add =
    function (oVec3f) {
        return new Vec3f(oVec3f.x + this.x, oVec3f.y + this.y, oVec3f.z + this.z);
    };

/**
 * Returns the value of the passed value subtracted, component-wise, from the object.
 * @param {Vec3f} oVec3f The Vec3f object
 * @returns {Vec3f}
 */
Vec3f.prototype.subtract =
    function (oVec3f) {
        return new Vec3f(this.x - oVec3f.x, this.y - oVec3f.y, this.z - oVec3f.z);
    };

/**
 * Returns the dot product of this vector and the passed value.
 * @param {Vec3f} oVec3f The Vec3f object
 * @returns {number}
 */
Vec3f.prototype.dot =
    function (oVec3f) {
        return (oVec3f.x * this.x + oVec3f.y * this.y + oVec3f.z * this.z);
    };

/**
 * Returns the cross product of the object and the passed value.
 * @param {Vec3f} oVec3f The Vec3f object
 * @returns {Vec3f}
 */
Vec3f.prototype.cross =
    function (oVec3f) {
        return new Vec3f(this.y * oVec3f.z - oVec3f.y * this.z, this.z * oVec3f.x - oVec3f.z * this.x, this.x * oVec3f.y - oVec3f.x * this.y);
    };

/**
 * Returns the value of the object divided by the passed value.
 * @param {number} fValue
 * @returns {Vec3f}
 */
Vec3f.prototype.divide =
    function (fValue) {
        return new Vec3f(this.x / fValue, this.y / fValue, this.z / fValue);
    };

/**
 * Returns the value of the object multiplied by the passed value.
 * @param {number} fValue
 * @returns {Vec3f}
 */
Vec3f.prototype.multiply =
    function (fValue) {
        return new Vec3f(this.x * fValue, this.y * fValue, this.z * fValue);
    };

/**
 * Returns the geometric length of this vector.
 * @returns {number}
 */
Vec3f.prototype.length =
    function () {
        return Math.sqrt(this.x * this.x + this.y * this.y + this.z * this.z);
    };

/**
 * Returns the object converted to unit length .
 * @returns {Vec3f}
 */
Vec3f.prototype.normalize =
    function () {
        var m = Math.sqrt(this.x * this.x + this.y * this.y + this.z * this.z);
        return m === 0 ? this : this.divide(m);
    };

/**
 * Returns the value of the component-wise negation of the object.
 * @returns {Vec3f}
 */
Vec3f.prototype.negate =
    function () {
        return new Vec3f(-this.x, -this.y, -this.z);
    };

module.exports = Vec3f;